// © Microsoft Corporation. All rights reserved.

import { classNames } from "@microsoft/fast-web-utilities";
import { Content } from "@msnews/social-data-service";
import { enums, openGuidelinePage, addOrUpdateTelemetryObject, renderHybrid } from "@msnews/social-utils";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import React from "react";
import { SocialCommentLocalizedStrings } from "../../SocialComment.localizedStrings";
import { CommentConnector, ComponentConnector } from "../../SocialComment.props";
import TypingUsers from "../typing-users/TypingUsers";
import { ContentPanelClassNameContract } from "./ContentPanel.classnames";
import { contentPanelTelemetryContracts, ContentPanelTelemetryObjects } from "./ContentPanel.telemetry";

export interface ContentPanelProps {
    managedClasses: ContentPanelClassNameContract;
    t: SocialCommentLocalizedStrings;
    componentConnector: ComponentConnector;
    commentConnector: CommentConnector;
    content: Content;
    responsiveClass: string;
    isExpanded: boolean;
    mainTelemetryObject: TelemetryObjectBase;
    commentDisplayType?: string;
    market?: string;
    theme: string;
}
export default class ContentPanel extends React.Component<ContentPanelProps> {
    telemetryObjects: ContentPanelTelemetryObjects;

    constructor(props) {
        super(props);
    }

    render() {
        const totalCommentCount = this.props.content.totalCommentCount;
        if (this.props.isExpanded) { return this.renderFullState(); }
        if (totalCommentCount == 0) {
            return this.renderZeroState();
        } else if (!this.props.isExpanded) {
            return this.renderMinimalState();
        } else {
            return this.renderFullState();
        }
    }

    renderZeroState() {
        const { t, content } = this.props;
        return (
            <div className={classNames(
                this.props.managedClasses.contentPanelContainer,
                this.props.managedClasses.contentPanelZeroStateContainer
            )}>
                <header className="cwt-content-panel-header">
                    <div className="cwt-content-panel-zero-state">
                        <h3 className="cwt-title">{content.conversationTitle}</h3>
                    </div>
                </header>
            </div>
        );
    }

    renderMinimalState() {
        const { t, content, responsiveClass } = this.props;
        const { listenForTypers, updateTypers } = this.props.commentConnector;
        const typingUserProps = { listenForTypers, updateTypers, managedClasses: this.props.managedClasses, t, content, noText: true };

        return (
            <div className={classNames(
                this.props.managedClasses.contentPanelMinimalStateContainer,
                responsiveClass
            )}>
                <TypingUsers {...typingUserProps} />
                <div className="cwt-beta-badge">BETA</div>
            </div>
        );
    }

    renderFullState() {
        const { t, content, mainTelemetryObject, commentDisplayType, market, componentConnector, theme} = this.props;
        const { conversationTitle, totalCommentCount } = content;
        const { listenForTypers, updateTypers } = this.props.commentConnector;
        const isCommentOverlay = commentDisplayType == "overlay";
        const isMobile = this.props.responsiveClass?.includes(enums.responsiveLevels.MobilePopup);
        const typingUserProps = { listenForTypers, updateTypers, managedClasses: this.props.managedClasses, t, content, noText: isCommentOverlay || isMobile };

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            guidelinesLink: addOrUpdateTelemetryObject(mainTelemetryObject, {...contentPanelTelemetryContracts.guidelinesLink}, { id: this.props.content && this.props.content.cmsId }, this.telemetryObjects && this.telemetryObjects.guidelinesLink)
        };

        return (
            <div className={classNames(
                this.props.managedClasses.contentPanelContainer,
                [this.props.managedClasses.contentPanelOverlayContainer, isCommentOverlay]
            )}>
                <header className="cwt-content-panel-header">
                    <h3 className="cwt-title">{conversationTitle}</h3>
                    <div className="cwt-beta-badge">BETA</div>
                </header>
                <div className="cwt-content-panel-content">
                    <h4 className="cwt-title">{t.ContentPanelCommentsTxt} {totalCommentCount > 0 ? <span className="cwt-comment-count">{totalCommentCount}</span> : ""}</h4>
                </div>
                <section className="cwt-guideline-section">
                    <footer className="cwt-content-panel-footer">
                        <span className="cwt-guidelines-message">
                            {t.ReferToGuidelinesMsgBefore}
                            <a className="cwt-link cwt-guidelines-link"
                                {...openGuidelinePage(componentConnector, theme, market, "_blank")}
                                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.guidelinesLink, false)}
                            >
                                {t.ShowGuidelinesLink}
                            </a>
                            {t.ReferToGuidelinesMsgAfter}
                        </span>
                    </footer>
                    <div className="cwt-main-typing-indicator">
                        <TypingUsers {...typingUserProps}/>
                    </div>
                </section>
            </div>
        );
    }
}